import dayjs from 'dayjs/esm';
import utc from 'dayjs/esm/plugin/utc';
import timezone from 'dayjs/esm/plugin/timezone';

// Self-contained init so these helpers work regardless of import order. Safe to
// call repeatedly (dayjs.extend no-ops on the same plugin).
dayjs.extend(utc);
dayjs.extend(timezone);

const ZONELESS_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** True for an ISO datetime string with no zone designator (no `Z`, no `±hh:mm`). */
function isZonelessIso(value: string): boolean {
  return ZONELESS_ISO.test(value.trim());
}

/** Zone-less wall-clock format (no `Z`, no offset) for the appointment write path. */
const WALL_CLOCK_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/**
 * Wall-clock to send on the appointment write path, from a datetime-**picker**
 * value.
 *
 * Appointment `start`/`end` are stored as **zone-less wall-clock** (the practice
 * clock, no zone) — for a future booking the wall time is the intent, and a UTC
 * instant would drift if the zone's DST rules change before the date. So the
 * write must send the **bare wall the user picked**, never a converted instant.
 *
 * A picker value's device-local components already hold the practice wall-clock
 * (see {@link instantToPracticeWallDate} for the matching pre-fill), so we just
 * format those components as a zone-less string.
 *
 * Returned typed `Date` to match the write model's field — it is a zone-less ISO
 * **string** on the wire (the entity types these wire fields as `Date`).
 */
export function pickerToWallClock(value: Date | string | number): Date {
  return dayjs(value).format(WALL_CLOCK_FORMAT) as unknown as Date;
}

/**
 * Wall-clock to send on the write path, derived from a true **instant** — e.g. a
 * FullCalendar drag/resize, whose dates are anchored to the practice zone. Emits
 * the instant's practice-zone wall-clock as a zone-less string. Same
 * string-typed-as-`Date` wire contract as {@link pickerToWallClock}.
 */
export function instantToWallClock(instant: Date | string | number, zone: string): Date {
  return dayjs(instant).tz(zone).format(WALL_CLOCK_FORMAT) as unknown as Date;
}

/**
 * Build a JS `Date` whose **device-local** wall-clock equals the practice
 * wall-clock of `value`, so a timezone-unaware picker or `| date` renders the
 * practice time. The pre-fill counterpart of {@link pickerToWallClock}.
 *
 * `value` may be:
 *  - a **zone-less** ISO string (`2026-06-09T10:00:00`) — how the backend serves
 *    appointment `start`/`end`: already practice wall-clock, so its own
 *    components are used directly; or
 *  - a true instant (`Date`, epoch ms, or zoned string) — converted to its
 *    practice-zone wall-clock first.
 *
 * For an in-zone viewer this is a no-op (the practice numbers already match the
 * device numbers), so SA users see no behavioural change.
 */
export function instantToPracticeWallDate(value: Date | string | number, zone: string): Date {
  const v = typeof value === 'string' ? value.trim() : value;
  // A zone-less string IS practice wall time — parse it AS wall time in the zone,
  // never as a device-local instant (which would mis-shift for out-of-zone
  // viewers). Anything carrying a real instant is converted into the zone.
  const pw = typeof v === 'string' && isZonelessIso(v) ? dayjs.tz(v, zone) : dayjs(v).tz(zone);
  return new Date(pw.year(), pw.month(), pw.date(), pw.hour(), pw.minute(), pw.second());
}

/**
 * Resolve a **practice-local** value to the true UTC instant it represents.
 *
 * A zone-less string (`2026-06-09T10:00:00`) is read as wall time in `zone`
 * (how the backend serves appointment `start`/`end`); anything already carrying
 * an instant (`Date`, epoch ms, or zoned string) is returned as that instant.
 *
 * Use this when you need to compare or place practice-local times on a real
 * timeline (e.g. matching appointments to day-view slots) so the comparison is
 * device-zone independent.
 */
export function practiceLocalToInstant(value: Date | string | number, zone: string): Date {
  const v = typeof value === 'string' ? value.trim() : value;
  return typeof v === 'string' && isZonelessIso(v) ? dayjs.tz(v, zone).toDate() : dayjs(v).toDate();
}

/**
 * Short zone abbreviation for an instant, e.g. `"SAST"` (or `"GMT+2"` where no
 * abbreviation exists). For the inline dual-time hint on appointment detail.
 */
export function formatZoneAbbrev(zone: string, instant: Date = new Date()): string {
  try {
    const name = new Intl.DateTimeFormat('en-ZA', {timeZone: zone, timeZoneName: 'short'})
      .formatToParts(instant).find(p => p.type === 'timeZoneName')?.value;
    return name ?? zone;
  } catch {
    return zone;
  }
}

/**
 * Fuller zone label for the calendar's persistent "Times shown in …" chip,
 * e.g. `"SAST (GMT+2)"`. Falls back to just the abbreviation, then the raw zone.
 */
export function formatZoneLabel(zone: string, instant: Date = new Date()): string {
  try {
    const short = formatZoneAbbrev(zone, instant);
    const offset = new Intl.DateTimeFormat('en-ZA', {timeZone: zone, timeZoneName: 'shortOffset'})
      .formatToParts(instant).find(p => p.type === 'timeZoneName')?.value;
    return offset && offset !== short ? `${short} (${offset})` : short;
  } catch {
    return zone;
  }
}

/** True when `zone` and `device` resolve to a different UTC offset for `instant` (DST-aware). */
export function zonesDifferAt(instant: Date, zone: string, device: string): boolean {
  if (zone === device) {
    return false;
  }
  return dayjs(instant).tz(zone).utcOffset() !== dayjs(instant).tz(device).utcOffset();
}
